import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Users, MapPin, CalendarPlus, Trash2, ChevronRight, CalendarIcon, Crosshair, ShieldCheck, Clock, Settings2 } from "lucide-react";
import { FieldGroupRoster } from "./FieldGroupRoster";
import { AdminOverrideDialog } from "./AdminOverrideDialog";
import { BackdateGrantsDialog } from "./BackdateGrantsDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface FieldSite { id: string; name: string; latitude: number; longitude: number; radius_m: number; active: boolean; }
interface Group {
  id: string;
  semester: string;
  class: string;
  name: string;
  site_id: string | null;
  site_name_override: string | null;
  lat_override: number | null;
  lng_override: number | null;
  radius_override_m: number | null;
  leader_student_id: string | null;
  approval_status: string;
  approved_at: string | null;
  scan_start_time: string | null;
  scan_end_time: string | null;
  max_radius_m: number | null;
  field_sites: { name: string } | null;
  member_count?: number;
}
interface PlacementDate { id: string; placement_date: string; }

const SEMESTERS = ["I", "II"];
const DEFAULT_CLASSES = ["CMT4", "CMT5", "CMT6", "PST4", "PST5", "PST6", "SW4", "SW5", "SW6"];

function groupConsecutiveDates(dates: PlacementDate[]): PlacementDate[][] {
  const sorted = [...dates].sort((a, b) => a.placement_date.localeCompare(b.placement_date));
  const groups: PlacementDate[][] = [];
  for (const d of sorted) {
    const last = groups[groups.length - 1];
    if (!last) { groups.push([d]); continue; }
    const prev = new Date(last[last.length - 1].placement_date);
    const cur = new Date(d.placement_date);
    const diff = (cur.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) last.push(d); else groups.push([d]);
  }
  return groups;
}

export function FieldGroupsManager() {
  const { user, roles } = useAuth();
  const isSuperadmin = (roles ?? []).includes("superadmin" as any);
  const [semester, setSemester] = useState<string>("I");
  const [klass, setKlass] = useState<string>("");
  const [classes, setClasses] = useState<string[]>([]);
  const [sites, setSites] = useState<FieldSite[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    site_id: "__none__" as string,
    site_name_override: "",
    lat_override: "",
    lng_override: "",
    radius_override_m: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [dates, setDates] = useState<PlacementDate[]>([]);
  const [newRange, setNewRange] = useState<{ from?: Date; to?: Date } | undefined>();
  const [pendingDelete, setPendingDelete] = useState<PlacementDate[] | null>(null);
  const [deletingDates, setDeletingDates] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesForm, setRulesForm] = useState({ scan_start_time: "", scan_end_time: "", max_radius_m: "" });
  const [excludeWeekends, setExcludeWeekends] = useState<boolean>(() => {
    try { return localStorage.getItem("rotation.excludeWeekends") !== "0"; } catch { return true; }
  });
  const [holidays, setHolidays] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("rotation.holidays") ?? "[]"); } catch { return []; }
  });
  const rosterRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { try { localStorage.setItem("rotation.excludeWeekends", excludeWeekends ? "1" : "0"); } catch {} }, [excludeWeekends]);
  useEffect(() => { try { localStorage.setItem("rotation.holidays", JSON.stringify(holidays)); } catch {} }, [holidays]);

  async function loadClasses() {
    // Try students table first (admins). Fall back to public modules list, then to defaults.
    const { data: studentRows } = await supabase.from("students").select("class").not("class", "is", null);
    let set = Array.from(new Set((studentRows ?? []).map((r: any) => r.class).filter(Boolean))) as string[];
    if (set.length === 0) {
      const { data: modRows } = await supabase.from("modules_public" as any).select("class");
      set = Array.from(new Set((modRows ?? []).map((r: any) => r.class).filter(Boolean))) as string[];
    }
    if (set.length === 0) set = [...DEFAULT_CLASSES];
    setClasses(set.sort());
  }

  async function loadSites() {
    const { data } = await supabase.from("field_sites").select("*").eq("active", true).order("name");
    setSites((data ?? []) as FieldSite[]);
  }

  async function loadGroups() {
    if (!klass) { setGroups([]); return; }
    const { data, error } = await supabase
      .from("field_groups")
      .select("*, field_sites(name)")
      .eq("semester", semester)
      .eq("class", klass)
      .order("name");
    if (error) { toast.error(error.message); return; }
    const baseGroups = (data ?? []) as any[];
    if (baseGroups.length === 0) { setGroups([]); return; }
    // Fetch counts
    const ids = baseGroups.map((g) => g.id);
    const { data: counts } = await supabase
      .from("field_group_members")
      .select("group_id")
      .in("group_id", ids);
    const countMap = new Map<string, number>();
    (counts ?? []).forEach((r: any) => countMap.set(r.group_id, (countMap.get(r.group_id) ?? 0) + 1));
    const merged = baseGroups.map((g) => ({ ...g, member_count: countMap.get(g.id) ?? 0 }));
    setGroups(merged);
    setSelectedGroup((prev) => {
      if (!prev) return prev;
      const fresh = merged.find((g) => g.id === prev.id);
      return fresh ? (fresh as Group) : prev;
    });
  }

  async function loadDates(groupId: string) {
    const { data } = await supabase
      .from("field_group_dates")
      .select("id, placement_date")
      .eq("group_id", groupId)
      .order("placement_date");
    setDates((data ?? []) as PlacementDate[]);
  }

  useEffect(() => { loadClasses(); loadSites(); }, []);
  useEffect(() => { loadGroups(); setSelectedGroup(null); }, [semester, klass]);
  useEffect(() => {
    if (selectedGroup) {
      loadDates(selectedGroup.id);
      // Smoothly scroll to the roster panel after it mounts
      requestAnimationFrame(() => {
        rosterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      setDates([]);
    }
  }, [selectedGroup?.id]);

  function openCreate() {
    setForm({ name: "", site_id: "__none__", site_name_override: "", lat_override: "", lng_override: "", radius_override_m: "" });
    setOpen(true);
  }

  function captureLocation() {
    if (!navigator.geolocation) { toast.error("Geolocation not available"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat_override: pos.coords.latitude.toFixed(6), lng_override: pos.coords.longitude.toFixed(6) })),
      (err) => toast.error("Location error: " + err.message),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function createGroup() {
    if (!user || !klass) return;
    if (!form.name.trim()) { toast.error("Group name is required"); return; }
    const usingSite = form.site_id !== "__none__";
    const usingOverride = !!(form.lat_override && form.lng_override);
    if (!usingSite && !usingOverride) {
      toast.error("Pick a field site OR provide override coordinates");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("field_groups").insert({
      semester,
      class: klass,
      name: form.name.trim(),
      site_id: usingSite ? form.site_id : null,
      site_name_override: usingOverride ? (form.site_name_override.trim() || null) : null,
      lat_override: usingOverride ? Number(form.lat_override) : null,
      lng_override: usingOverride ? Number(form.lng_override) : null,
      radius_override_m: usingOverride && form.radius_override_m ? Number(form.radius_override_m) : null,
      created_by: user.id,
    });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Group created");
    setOpen(false);
    loadGroups();
  }

  async function deleteGroup(g: Group) {
    if (!confirm(`Delete group "${g.name}"? All members and dates will be removed.`)) return;
    const { error } = await supabase.from("field_groups").delete().eq("id", g.id);
    if (error) { toast.error(error.message); return; }
    if (selectedGroup?.id === g.id) setSelectedGroup(null);
    loadGroups();
  }

  async function addDate() {
    if (!selectedGroup || !newRange?.from) return;
    const start = newRange.from;
    const end = newRange.to ?? newRange.from;
    const days: string[] = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const holidaySet = new Set(holidays);
    let weekendSkipped = 0;
    let holidaySkipped = 0;
    while (cur <= last) {
      const iso = format(cur, "yyyy-MM-dd");
      const dow = cur.getDay(); // 0=Sun, 6=Sat
      if (excludeWeekends && (dow === 0 || dow === 6)) {
        weekendSkipped++;
      } else if (holidaySet.has(iso)) {
        holidaySkipped++;
      } else {
        days.push(iso);
      }
      cur.setDate(cur.getDate() + 1);
    }
    const existing = new Set(dates.map((d) => d.placement_date));
    const rows = days.filter((d) => !existing.has(d)).map((d) => ({ group_id: selectedGroup.id, placement_date: d }));
    if (rows.length === 0) {
      const reason = weekendSkipped + holidaySkipped > 0
        ? ` (skipped ${weekendSkipped} weekend day${weekendSkipped===1?"":"s"} and ${holidaySkipped} holiday${holidaySkipped===1?"":"s"})`
        : "";
      toast.info(`No new dates to add${reason}`);
      return;
    }
    const { error } = await supabase.from("field_group_dates").insert(rows);
    if (error) { toast.error(error.message); return; }
    const extra = weekendSkipped + holidaySkipped > 0
      ? ` · skipped ${weekendSkipped} weekend, ${holidaySkipped} holiday`
      : "";
    toast.success(`Added ${rows.length} date${rows.length > 1 ? "s" : ""}${extra}`);
    setNewRange(undefined);
    loadDates(selectedGroup.id);
  }

  function toggleHoliday(d: Date | undefined) {
    if (!d) return;
    const iso = format(d, "yyyy-MM-dd");
    setHolidays((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort());
  }

  async function removeDate(d: PlacementDate) {
    const { error } = await supabase.from("field_group_dates").delete().eq("id", d.id);
    if (error) { toast.error(error.message); return; }
    if (selectedGroup) loadDates(selectedGroup.id);
  }

  function openRules(g: Group) {
    setSelectedGroup(g);
    setRulesForm({
      scan_start_time: g.scan_start_time?.slice(0, 5) ?? "",
      scan_end_time: g.scan_end_time?.slice(0, 5) ?? "",
      max_radius_m: g.max_radius_m?.toString() ?? "",
    });
    setRulesOpen(true);
  }

  async function saveRules() {
    if (!selectedGroup) return;
    const { scan_start_time, scan_end_time, max_radius_m } = rulesForm;
    if ((scan_start_time && !scan_end_time) || (!scan_start_time && scan_end_time)) {
      toast.error("Provide both start AND end time, or leave both blank.");
      return;
    }
    if (scan_start_time && scan_end_time && scan_start_time >= scan_end_time) {
      toast.error("Start time must be before end time.");
      return;
    }
    const { error } = await supabase.from("field_groups").update({
      scan_start_time: scan_start_time || null,
      scan_end_time: scan_end_time || null,
      max_radius_m: max_radius_m ? Number(max_radius_m) : null,
    }).eq("id", selectedGroup.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Rules saved");
    setRulesOpen(false);
    loadGroups();
  }

  async function setApproval(g: Group, approve: boolean) {
    if (approve && !g.scan_start_time && !g.scan_end_time && !g.max_radius_m && !g.leader_student_id) {
      if (!confirm("This group has no scan rules and no leader yet. Approve anyway?")) return;
    }
    const { error } = await supabase.from("field_groups").update({
      approval_status: approve ? "approved" : "draft",
      approved_by: approve ? user!.id : null,
      approved_at: approve ? new Date().toISOString() : null,
    }).eq("id", g.id);
    if (error) { toast.error(error.message); return; }
    toast.success(approve ? "Group approved — leader can now scan" : "Approval revoked");
    loadGroups();
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Semester</Label>
            <Select value={semester} onValueChange={setSemester}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SEMESTERS.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Class</Label>
            <Select value={klass} onValueChange={setKlass}>
              <SelectTrigger><SelectValue placeholder="Pick a class…" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} disabled={!klass} className="w-full">
                  <Plus className="w-4 h-4 mr-1" /> New Field Group
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>New field group</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Group name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Group A — Muhimbili NH" />
                  </div>
                  <div>
                    <Label className="text-xs">Field site</Label>
                    <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Use override coordinates —</SelectItem>
                        {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="border rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium">Override (ad-hoc placement)</p>
                    <p className="text-[11px] text-muted-foreground">Fill these to override the catalogue site, or skip if you picked a site above.</p>
                    <Input placeholder="Site name (optional)" value={form.site_name_override} onChange={(e) => setForm({ ...form, site_name_override: e.target.value })} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" step="any" placeholder="Latitude" value={form.lat_override} onChange={(e) => setForm({ ...form, lat_override: e.target.value })} />
                      <Input type="number" step="any" placeholder="Longitude" value={form.lng_override} onChange={(e) => setForm({ ...form, lng_override: e.target.value })} />
                    </div>
                    <Input type="number" placeholder="Radius (meters, default 150)" value={form.radius_override_m} onChange={(e) => setForm({ ...form, radius_override_m: e.target.value })} />
                    <Button type="button" variant="outline" size="sm" onClick={captureLocation}>
                      <Crosshair className="w-3 h-3 mr-1" /> Use my current location
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={createGroup} disabled={loading}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {klass && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> Groups</CardTitle>
              <CardDescription>
                {groups.length} group(s) in {klass}, Semester {semester}.
                <span className="block mt-1 text-xs">
                  Each group's leader scans rotation-based attendance using the <strong>Academic QR code</strong>, restricted to the group's geofence and scan window.
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No groups yet.</TableCell></TableRow>
                  ) : groups.map((g) => {
                    const siteLabel = g.field_sites?.name ?? g.site_name_override ?? (g.lat_override ? "Ad-hoc" : "—");
                    const isSelected = selectedGroup?.id === g.id;
                    const approved = g.approval_status === "approved";
                    const window = g.scan_start_time && g.scan_end_time
                      ? `${g.scan_start_time.slice(0,5)}–${g.scan_end_time.slice(0,5)}`
                      : null;
                    return (
                      <TableRow key={g.id} className={cn(isSelected && "bg-muted/50")}>
                        <TableCell className="font-medium">
                          {g.name}
                          {window && (
                            <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 ml-2">
                              <Clock className="h-3 w-3" />{window}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{siteLabel}</span></TableCell>
                        <TableCell><Badge variant="secondary">{g.member_count ?? 0}</Badge></TableCell>
                        <TableCell>
                          {approved
                            ? <Badge className="bg-primary/15 text-primary hover:bg-primary/20"><ShieldCheck className="h-3 w-3 mr-1" />Approved</Badge>
                            : <Badge variant="outline">Draft</Badge>}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Scan rules" onClick={() => openRules(g)}>
                            <Settings2 className="h-4 w-4" />
                          </Button>
                          {approved ? (
                            <Button variant="outline" size="sm" onClick={() => setApproval(g, false)}>Revoke</Button>
                          ) : (
                            <Button size="sm" onClick={() => setApproval(g, true)}>
                              <ShieldCheck className="h-3 w-3 mr-1" />Approve
                            </Button>
                          )}
                          <AdminOverrideDialog groupId={g.id} groupName={g.name} onSaved={loadGroups} />
                          {isSuperadmin && <BackdateGrantsDialog groupId={g.id} groupName={g.name} />}
                          <Button variant="ghost" size="sm" onClick={() => setSelectedGroup(g)}>
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteGroup(g)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Rules dialog */}
          <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Scan rules — {selectedGroup?.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Configure when and where the group leader is allowed to scan. These rules are enforced both in the app and on the server.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Earliest scan time</Label>
                    <Input type="time" value={rulesForm.scan_start_time} onChange={(e) => setRulesForm({ ...rulesForm, scan_start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Latest scan time</Label>
                    <Input type="time" value={rulesForm.scan_end_time} onChange={(e) => setRulesForm({ ...rulesForm, scan_end_time: e.target.value })} />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">Leave both blank to allow scans any time of day.</p>
                <div>
                  <Label className="text-xs">Maximum allowed radius (m)</Label>
                  <Input type="number" min={10} placeholder="e.g. 200 (caps the site radius)" value={rulesForm.max_radius_m} onChange={(e) => setRulesForm({ ...rulesForm, max_radius_m: e.target.value })} />
                  <p className="text-[11px] text-muted-foreground mt-1">If set, this caps the effective geofence even if the catalogue site has a wider radius.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRulesOpen(false)}>Cancel</Button>
                <Button onClick={saveRules}>Save rules</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {selectedGroup && (
            <div ref={rosterRef} className="space-y-4 scroll-mt-20">
              <FieldGroupRoster group={selectedGroup} onChanged={loadGroups} />
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><CalendarPlus className="w-4 h-4" /> Placement Dates</CardTitle>
                  <CardDescription>Days when this group is on placement at the site.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="border rounded-md p-2 space-y-2 bg-muted/30">
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                        <Checkbox checked={excludeWeekends} onCheckedChange={(v) => setExcludeWeekends(!!v)} />
                        Exclude weekends (Sat & Sun)
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            Holidays to skip ({holidays.length})
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="multiple"
                            selected={holidays.map((s) => new Date(s))}
                            onSelect={(dates) => {
                              const arr = (dates ?? []) as Date[];
                              setHolidays(arr.map((d) => format(d, "yyyy-MM-dd")).sort());
                            }}
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                      {holidays.length > 0 && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => setHolidays([])}>
                          Clear holidays
                        </Button>
                      )}
                    </div>
                    {holidays.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {holidays.map((h) => (
                          <Badge key={h} variant="outline" className="text-[10px]">
                            {format(new Date(h), "PP")}
                            <button className="ml-1 hover:text-destructive" onClick={() => toggleHoliday(new Date(h))}>×</button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      When you add a date range below, weekends and listed holidays will be skipped automatically. Holidays are remembered on this device.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    <div className="flex-1 space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">From date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !newRange?.from && "text-muted-foreground")}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newRange?.from ? format(newRange.from, "PP") : "Pick start date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newRange?.from}
                            onSelect={(d) => setNewRange((prev: any) => ({
                              from: d,
                              to: prev?.to && d && prev.to >= d ? prev.to : d,
                            }))}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex-1 space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">To date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            disabled={!newRange?.from}
                            className={cn("w-full justify-start text-left font-normal", !newRange?.to && "text-muted-foreground")}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newRange?.to ? format(newRange.to, "PP") : "Pick end date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newRange?.to}
                            onSelect={(d) => setNewRange((prev: any) => ({ from: prev?.from, to: d }))}
                            disabled={(date) => !!newRange?.from && date < newRange.from}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Button onClick={addDate} disabled={!newRange?.from} className="sm:w-auto">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {dates.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No dates scheduled yet.</p>
                    ) : groupConsecutiveDates(dates).map((grp) => (
                      <Badge key={grp[0].id} variant="secondary" className="gap-2">
                        {grp.length === 1
                          ? format(new Date(grp[0].placement_date), "PP")
                          : `${format(new Date(grp[0].placement_date), "MMM d")} – ${format(new Date(grp[grp.length - 1].placement_date), "PP")}`}
                        <button
                          onClick={() => setPendingDelete(grp)}
                          className="hover:text-destructive"
                          title={grp.length > 1 ? `Remove ${grp.length} days` : "Remove day"}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>

                  <AlertDialog
                    open={!!pendingDelete}
                    onOpenChange={(o) => {
                      if (!o && !deletingDates) { setPendingDelete(null); setDeleteConfirmText(""); }
                    }}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove placement date{(pendingDelete?.length ?? 0) > 1 ? "s" : ""}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {pendingDelete && pendingDelete.length === 1
                            ? <>This will remove <strong>{format(new Date(pendingDelete[0].placement_date), "PP")}</strong> from this group's schedule.</>
                            : pendingDelete && (
                              <>This will remove <strong>{pendingDelete.length} days</strong> ({format(new Date(pendingDelete[0].placement_date), "MMM d")} – {format(new Date(pendingDelete[pendingDelete.length - 1].placement_date), "PP")}) from this group's schedule. Attendance already recorded on these days will not be deleted.</>
                            )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      {pendingDelete && pendingDelete.length > 1 && (
                        <div className="space-y-2">
                          <Label htmlFor="delete-confirm" className="text-xs">
                            Type <span className="font-mono font-semibold text-destructive">DELETE</span> to confirm removing this range.
                          </Label>
                          <Input
                            id="delete-confirm"
                            autoFocus
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                            disabled={deletingDates}
                          />
                        </div>
                      )}
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingDates}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          disabled={
                            deletingDates ||
                            (!!pendingDelete && pendingDelete.length > 1 && deleteConfirmText.trim() !== "DELETE")
                          }
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={async (e) => {
                            e.preventDefault();
                            if (!pendingDelete) return;
                            if (pendingDelete.length > 1 && deleteConfirmText.trim() !== "DELETE") return;
                            setDeletingDates(true);
                            for (const d of pendingDelete) await removeDate(d);
                            setDeletingDates(false);
                            setPendingDelete(null);
                            setDeleteConfirmText("");
                          }}
                        >
                          {deletingDates ? "Removing…" : "Remove"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
